/**
 * PortalSAMLCallbackPage — canjea el token de un solo uso tras el SSO del portal.
 *
 * Ruta: /colaboradores/callback/saml?token=<otp>
 *
 * El backend resuelve el email de la aserción a una de dos identidades y lo dice
 * en `kind`, así que el destino depende de eso:
 *   - `portal` → cuenta propia del colaborador (Caso A) → portalAuthStore
 *   - `erp`    → ya tiene usuario del ERP (Caso B) → authStore y /portal (el inicio)
 */
import { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import apiClient from '@/lib/apiClient'
import { usePortalAuthStore } from '@/store/portalAuthStore'
import { useAuthStore } from '@/store/authStore'
import { enElPortal, tomarDestino } from '@/lib/destinoTrasLogin'
import { tenantSegunHost } from '@/lib/tenantNav'

export default function PortalSAMLCallbackPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { setTokens: setPortalTokens } = usePortalAuthStore()
  const { setTokens: setErpTokens, setTenants } = useAuthStore()
  const processed = useRef(false)

  useEffect(() => {
    if (processed.current) return
    processed.current = true

    const token = searchParams.get('token')
    if (!token) {
      navigate('/colaboradores/saml-error?code=missing_token', { replace: true })
      return
    }

    apiClient
      .get(`/auth/saml/callback/?token=${encodeURIComponent(token)}`)
      .then((res) => {
        const data = res.data
        // El enlace que se estaba abriendo (#1656). Las dos puertas del portal
        // tienen las mismas pantallas, así que se traduce a la que corresponde.
        const destino = tomarDestino()
        if (data.kind === 'erp') {
          setErpTokens(data.access)
          if (data.tenants?.length) {
            setTenants(data.tenants)
            // El portal se abrió en el subdominio de una empresa: es esa. Sin
            // esto, quien tiene varias empresas pasaba por el selector y
            // terminaba en el dashboard del ERP.
            const delHost = tenantSegunHost(data.tenants, null)
            if (delHost) useAuthStore.getState().setActiveTenant(delHost)
          }
          navigate(enElPortal(destino, 'portal'), { replace: true })
          return
        }
        setPortalTokens(data.access, data.refresh, data.employee)
        navigate(enElPortal(destino, 'colaboradores'), { replace: true })
      })
      .catch(() => {
        navigate('/colaboradores/saml-error?code=callback_failed', { replace: true })
      })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas">
      <div className="text-center">
        <Loader2 size={40} className="animate-spin text-primary mx-auto mb-4" />
        <p className="text-sm text-content-muted">Autenticando con SSO…</p>
      </div>
    </div>
  )
}
